import { memo, useMemo, isValidElement, type ReactElement } from 'react';
import { isMobile } from '@direct-frontend/platform-code';

import { Image } from '../../Image';
import { cn } from '../../../lib/css/className/className';
import type { PageHeadImage as PageHeadImageType } from '../types';
import type { TestProps } from '../../../internal/types/testTypes';

import './PageHeadImage.css';

const cls = cn('PageHeadImage');

const imageSize = 56;

interface PageHeadImageProps extends TestProps {
    image?: PageHeadImageType;
}

/**
 * Компонент отображения изображения в PageHead
 * @kind component
 */
export const PageHeadImage = memo<PageHeadImageProps>(function PageHeadImage({
    image,
    'data-testid': dataTestId = 'PageHeadImage',
}) {
    const content = useMemo(() => {
        if (!image) {
            return null;
        }

        if (isValidElement(image)) {
            return image;
        }

        const { label, ...imageProps }: Exclude<PageHeadImageType, string | ReactElement> =
            typeof image === 'string' ? { src: image, alt: '', label: undefined } : image;

        return (
            <>
                <Image
                    {...imageProps}
                    width={imageSize}
                    height={imageSize}
                    borderRadius={isMobile() ? '10' : '12'}
                    background="gray"
                />
                {label && (
                    <div className={cls('label')} data-testid={`${dataTestId}.ImageLabel`}>
                        {label}
                    </div>
                )}
            </>
        );
    }, [image, dataTestId]);

    if (!image) {
        return null;
    }

    return (
        <div className={cls({})} data-testid={dataTestId}>
            {content}
        </div>
    );
});
